// Activity Timeline Component for Memara
import React from 'react';
import { motion } from 'framer-motion';
import {
  useActivityTimeline,
  type ActivityFeedScope,
} from '../hooks/useActivityTimeline';
import { ActivityEvent } from '../services/activityTimeline';
import { Clock, Zap, Search, Plus, Trophy, Target, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ActivityTimelineProps {
  limit?: number;
  showHeader?: boolean;
  compact?: boolean;
  className?: string;
  activityFeedScope?: ActivityFeedScope;
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ 
  limit = 10, 
  showHeader = true, 
  compact = false,
  className = '',
  activityFeedScope = 'contextSpace',
}) => {
  const { recentActivities, isLoading } = useActivityTimeline({
    activityFeedScope,
  });
  const { t } = useTranslation();

  const getActivityIcon = (activity: ActivityEvent) => {
    switch (activity.type) {
      case 'memory_created':
        return <Plus className="w-4 h-4 text-green-500" />;
      case 'memory_updated':
        return <Zap className="w-4 h-4 text-blue-500" />;
      case 'memory_deleted':
        return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'search_performed':
        return <Search className="w-4 h-4 text-purple-500" />;
      case 'memory_listed':
      case 'memory_fetched':
        return <Search className="w-4 h-4 text-violet-500" />;
      case 'space_created':
        return <Target className="w-4 h-4 text-orange-500" />;
      case 'achievement_unlocked':
      case 'milestone_reached':
        return <Trophy className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityColor = (activity: ActivityEvent) => {
    switch (activity.impact) {
      case 'high':
        return 'border-l-green-500 bg-green-50';
      case 'medium':
        return 'border-l-blue-500 bg-blue-50';
      case 'low':
        return 'border-l-gray-500 bg-gray-50';
      default:
        return 'border-l-gray-300 bg-gray-50';
    }
  };

  const formatRelativeTime = (timestamp: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - timestamp.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return t('activityTimeline.justNow', { ns: 'dashboard' }) as string;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return t('activityTimeline.minutesAgo', { minutes: diffInMinutes, ns: 'dashboard' }) as string;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return t('activityTimeline.hoursAgo', { hours: diffInHours, ns: 'dashboard' }) as string;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return t('activityTimeline.daysAgo', { days: diffInDays, ns: 'dashboard' }) as string;
    }
    
    return timestamp.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {showHeader && (
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('activityTimeline.recentActivity', { ns: 'dashboard' }) as string}</h3>
          </div>
        )}
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-16 bg-gray-200 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const displayActivities = recentActivities.slice(0, limit);

  if (displayActivities.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        {showHeader && (
          <div className="flex items-center gap-2 justify-center mb-4">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('activityTimeline.recentActivity', { ns: 'dashboard' }) as string}</h3>
          </div>
        )}
        <div className="text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm">{t('activityTimeline.noRecentActivity', { ns: 'dashboard' }) as string}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t('activityTimeline.startCreatingMemories', { ns: 'dashboard' }) as string}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {showHeader && (
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('activityTimeline.recentActivity', { ns: 'dashboard' }) as string}</h3>
          <span className="text-sm text-gray-500 ml-auto">
            {t('activityTimeline.recentCount', { count: displayActivities.length, ns: 'dashboard' }) as string}
          </span>
        </div>
      )}

      <div className="space-y-2">
        {displayActivities.map((activity, index) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.05,
              ease: 'easeOut'
            }}
            className={`
              relative p-4 rounded-lg border-l-4 transition-all duration-200 hover:shadow-md
              ${getActivityColor(activity)}
              ${compact ? 'p-3' : 'p-4'}
            `}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getActivityIcon(activity)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h4 className={`font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
                      {activity.title}
                    </h4>
                    {!compact && (
                      <p className="text-sm text-gray-600 mt-1">
                        {activity.description}
                      </p>
                    )}
                    
                    {/* Metadata display */}
                    {activity.metadata && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {activity.metadata.spaceName && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                            📁 {activity.metadata.spaceName}
                          </span>
                        )}
                        {activity.metadata.searchQuery && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                            🔍 "{activity.metadata.searchQuery}"
                          </span>
                        )}
                        {activity.metadata.value && activity.type === 'search_performed' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            {activity.metadata.value} results
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 text-right">
                    <span className={`text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>
                      {formatRelativeTime(activity.timestamp)}
                    </span>
                    {activity.impact === 'high' && (
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-1 ml-auto"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Impact indicator */}
            <div className="absolute top-2 right-2">
              {activity.impact === 'high' && (
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {recentActivities.length > limit && (
        <div className="text-center pt-2">
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            {t('activityTimeline.viewAllActivities', { count: recentActivities.length, ns: 'dashboard' }) as string} →
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityTimeline; 